import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MODULE_REGISTRY } from './moduleRegistry';
import { IconButton } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';

/**
 * DashboardModule — wraps each active module with drag-and-drop + edit mode controls.
 *
 * Normal mode: transparent pass-through, zero visual overhead.
 * Edit mode: control bar with drag handle, size toggle, and remove button.
 */
const DashboardModule = ({ moduleConfig, editMode, onRemove, onToggleSize, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: moduleConfig.id, disabled: !editMode });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
  };

  const meta = MODULE_REGISTRY[moduleConfig.id];
  const isHalf = (moduleConfig.size || 'full') === 'half';

  // Normal mode — stretch child to fill grid cell height
  if (!editMode) {
    return <div className="h-full [&>*]:h-full">{children}</div>;
  }

  // Edit mode — drag handle + size toggle + remove
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        relative group rounded-md transition-all duration-300 h-full flex flex-col
        ${isDragging
          ? 'ring-2 ring-primary/60 shadow-[0_0_30px_rgba(255,204,51,0.15)] scale-[1.01]'
          : 'ring-1 ring-dashed ring-outline-variant hover:ring-outline'
        }
      `}
    >
      {/* ---- Edit mode control bar ---- */}
      <div className={`
        flex items-center gap-2 px-2 py-1.5 rounded-t-md
        transition-all duration-300
        ${isDragging
          ? 'bg-primary/10 border-b border-primary/20'
          : 'bg-surface-container-high border-b border-outline-variant'
        }
      `}>
        {/* Drag handle */}
        <button
          {...attributes}
          {...listeners}
          className={`
            flex-shrink-0 p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center
            rounded-md cursor-grab active:cursor-grabbing
            transition-all duration-200
            ${isDragging
              ? 'text-primary-ink bg-primary/10'
              : 'text-on-surface-variant hover:text-on-surface hover:bg-gray-700/50'
            }
          `}
          aria-label={`Drag to reorder ${meta?.label}`}
        >
          <svg className="w-4 h-4" viewBox="0 0 16 16" fill="currentColor">
            <circle cx="5" cy="3" r="1.2" />
            <circle cx="11" cy="3" r="1.2" />
            <circle cx="5" cy="8" r="1.2" />
            <circle cx="11" cy="8" r="1.2" />
            <circle cx="5" cy="13" r="1.2" />
            <circle cx="11" cy="13" r="1.2" />
          </svg>
        </button>

        {/* Module label */}
        <div className="flex-1 min-w-0">
          <span className={`
            text-xs font-semibold uppercase tracking-widest
            transition-colors duration-200
            ${isDragging ? 'text-primary' : 'text-on-surface-variant'}
          `}>
            {meta?.label || moduleConfig.id}
          </span>
        </div>

        {/* Size toggle */}
        <Tooltip label={isHalf ? 'Expand to full width' : 'Shrink to half width'}><IconButton
          onClick={(e) => { e.stopPropagation(); onToggleSize(moduleConfig.id); }}
          label={isHalf ? 'Expand to full width' : 'Shrink to half width'}
        >
          {isHalf ? (
            /* Expand icon */
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
            </svg>
          ) : (
            /* Shrink icon */
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
            </svg>
          )}
        </IconButton></Tooltip>

        {/* Remove button */}
        <Tooltip label={`Remove ${meta?.label} from dashboard`}><IconButton
          onClick={(e) => { e.stopPropagation(); onRemove(moduleConfig.id); }}
          label={`Remove ${meta?.label} from dashboard`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </IconButton></Tooltip>
      </div>

      {/* ---- Module content ---- */}
      <div className="p-1 flex-1">
        {children}
      </div>
    </div>
  );
};

export default DashboardModule;
